import Dexie from "dexie";
import { ITimeSlot } from "@/types/db/timeslots";
import { dexieDB } from "@/services/dexie-database";
import { LogLevel, LogService } from "@/services/logging";
import { recordOperationDuration } from "@/utils/vue-helpers/common";
import dayjs from "dayjs";

/**
 * Default lower bound for startDateTime queries: January 1, 2020
 * Default upper bound for startDateTime queries: 10 years in the future
 */
const DEFAULT_START_LOWER_BOUND = "2020-01-01T00:00:00.000Z";

const DEFAULT_END_UPPER_BOUND = new Date(
  new Date().getTime() + 10 * 365 * 24 * 60 * 60 * 1000
).toISOString();

/**
 * Options for querying timeslots by product category IDs
 */
export interface ITimeslotCategoryQueryOptions {
  /** Filter by product category IDs (required, uses categoryId index) */
  categoryIds: number[];

  /** Only return timeslots with startDateTime >= this value */
  startDateTimeAfter?: string;

  /** Only return timeslots with startDateTime <= this value */
  startDateTimeBefore?: string;

  /** Only return timeslots with endDateTime >= this value */
  endDateTimeAfter?: string;

  /** Only return timeslots with endDateTime < this value */
  endDateTimeBefore?: string;

  /** Number of records to skip (default: 0) */
  skip?: number;

  /** Maximum number of records to return */
  limit?: number;
}

/**
 * Options for querying timeslots by ticket group ID
 */
export interface ITimeslotTicketGroupQueryOptions {
  /** Filter by ticket group ID (uses multi-entry index on ticketGroups) */
  ticketGroupId: number;

  /** Only return timeslots with startDateTime >= this value */
  startDateTimeAfter?: string;

  /** Only return timeslots with startDateTime < this value */
  startDateTimeBefore?: string;

  /** Only return timeslots with endDateTime >= this value */
  endDateTimeAfter?: string;

  /** Only return timeslots with endDateTime < this value */
  endDateTimeBefore?: string;

  /** Number of records to skip (default: 0) */
  skip?: number;

  /** Maximum number of records to return */
  limit?: number;
}

/**
 * Query timeslots by product category IDs with flexible date filtering and pagination
 *
 * Always uses the [categoryId+startDateTime] compound index for optimal performance.
 * If no startDateTimeAfter is provided, defaults to "2020-01-01" as a safe lower bound.
 * endDateTime filters are applied in memory after the index query.
 *
 * For single category queries, the compound index is used directly.
 * For multiple category queries, anyOf with in-memory filtering is used.
 *
 * Filter semantics:
 * - startDateTimeAfter: >= comparison (inclusive)
 * - startDateTimeBefore: <= comparison (inclusive)
 * - endDateTimeAfter: >= comparison (inclusive)
 * - endDateTimeBefore: < comparison (exclusive)
 *
 * @param options - Query options with required categoryIds
 * @returns Array of timeslots sorted by startDateTime ascending
 *
 * @example
 * // Get future timeslots (not yet ended) for multiple products
 * const timeslots = await queryTimeslotsByProductCategories({
 *   categoryIds: [1, 2, 3],
 *   endDateTimeAfter: new Date().toISOString(),
 * });
 *
 * @example
 * // Get timeslots within a date range with pagination
 * const timeslots = await queryTimeslotsByProductCategories({
 *   categoryIds: [1],
 *   startDateTimeAfter: '2026-01-01T00:00:00Z',
 *   startDateTimeBefore: '2026-01-31T23:59:59Z',
 *   skip: 0,
 *   limit: 50,
 * });
 */
export async function queryTimeslotsByProductCategories(
  options: ITimeslotCategoryQueryOptions
): Promise<ITimeSlot[]> {
  const {
    categoryIds,
    startDateTimeAfter,
    startDateTimeBefore,
    endDateTimeAfter,
    endDateTimeBefore,
    skip = 0,
    limit,
  } = options;

  if (!categoryIds || categoryIds.length === 0) {
    return [];
  }

  const t1 = performance.now();

  try {
    let results: ITimeSlot[];

    // Use default lower bound if no startDateTimeAfter provided
    const effectiveStartAfter = startDateTimeAfter || DEFAULT_START_LOWER_BOUND;
    // Use default upper bound if no startDateTimeBefore provided
    const effectiveStartBefore = startDateTimeBefore || DEFAULT_END_UPPER_BOUND;

    // Check if we need to filter by endDateTime in memory
    const needsEndFilter = !!(endDateTimeAfter || endDateTimeBefore);

    if (categoryIds.length === 1) {
      // SINGLE CATEGORY - Use compound index [categoryId+startDateTime] directly
      const categoryId = categoryIds[0];

      if (needsEndFilter) {
        // With endDateTime filter: query index, filter in memory, then paginate
        results = await dexieDB.timeslots
          .where("[categoryId+startDateTime]")
          .between(
            [categoryId, effectiveStartAfter],
            [categoryId, effectiveStartBefore],
            true,
            true
          )
          .filter((ts) => {
            if (endDateTimeAfter && ts.endDateTime < endDateTimeAfter)
              return false;
            if (endDateTimeBefore && ts.endDateTime >= endDateTimeBefore)
              return false;
            return true;
          })
          .toArray();

        // Apply pagination after filtering
        if (skip > 0) {
          results = results.slice(skip);
        }
        if (limit !== undefined) {
          results = results.slice(0, limit);
        }
      } else {
        // No endDateTime filter: use offset/limit at DB level for best performance
        results = await dexieDB.timeslots
          .where("[categoryId+startDateTime]")
          .between(
            [categoryId, effectiveStartAfter],
            [categoryId, effectiveStartBefore],
            true,
            true
          )
          .offset(skip)
          .limit(limit ?? 10000) // large number if limit is undefined
          .toArray();
      }
    } else {
      // MULTIPLE CATEGORIES - Run parallel compound-index ([categoryId+startDateTime]) queries per category
      // and merge results.

      // endDateTime filter function (applied in-memory after index query)
      const endFilterFn = needsEndFilter
        ? (ts: ITimeSlot) => {
            if (endDateTimeAfter && ts.endDateTime < endDateTimeAfter)
              return false;
            if (endDateTimeBefore && ts.endDateTime >= endDateTimeBefore)
              return false;
            return true;
          }
        : null;

      const queries = categoryIds.map((categoryId) => {
        const query = dexieDB.timeslots
          .where("[categoryId+startDateTime]")
          .between(
            [categoryId, effectiveStartAfter],
            [categoryId, effectiveStartBefore],
            true,
            true
          );

        if (endFilterFn) {
          return query.filter(endFilterFn).toArray();
        }
        return query.toArray();
      });

      const perCategoryResults = await Promise.all(queries);

      // Merge and sort by startDateTime
      results = perCategoryResults.flat();
      results.sort((a, b) => a.startDateTime.localeCompare(b.startDateTime));

      // Apply pagination after sorting
      if (skip > 0) {
        results = results.slice(skip);
      }
      if (limit !== undefined) {
        results = results.slice(0, limit);
      }
    }

    const duration = performance.now() - t1;
    recordOperationDuration(
      "dexie.queryTimeslotsByProductCategories",
      duration
    );

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.queryTimeslotsByProductCategories",
      { params: options, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Query timeslots by ticket group ID with flexible date filtering and pagination
 *
 * Uses the multi-entry index on ticketGroups for efficient lookup.
 * Date filtering and sorting are applied in memory after the index query.
 *
 * Filter semantics:
 * - startDateTimeAfter: >= comparison (inclusive)
 * - startDateTimeBefore: < comparison (exclusive)
 * - endDateTimeAfter: >= comparison (inclusive)
 * - endDateTimeBefore: < comparison (exclusive)
 *
 * @param options - Query options with required ticketGroupId
 * @returns Array of timeslots sorted by startDateTime ascending
 *
 * @example
 * // Get future timeslots (not yet ended) for a ticket group
 * const timeslots = await queryTimeslotsByTicketGroup({
 *   ticketGroupId: 123,
 *   endDateTimeAfter: new Date().toISOString(),
 * });
 *
 * @example
 * // Get timeslots within a date range with pagination
 * const timeslots = await queryTimeslotsByTicketGroup({
 *   ticketGroupId: 123,
 *   startDateTimeAfter: '2026-01-01T00:00:00Z',
 *   startDateTimeBefore: '2026-01-31T23:59:59Z',
 *   skip: 0,
 *   limit: 50,
 * });
 */
export async function queryTimeslotsByTicketGroup(
  options: ITimeslotTicketGroupQueryOptions
): Promise<ITimeSlot[]> {
  const {
    ticketGroupId,
    startDateTimeAfter,
    startDateTimeBefore,
    endDateTimeAfter,
    endDateTimeBefore,
    skip = 0,
    limit,
  } = options;

  const t1 = performance.now();

  try {
    // Use default lower bound if no startDateTimeAfter provided
    const effectiveStartAfter = startDateTimeAfter || DEFAULT_START_LOWER_BOUND;

    let results: ITimeSlot[];

    // Optimization: when both start date bounds are provided
    // use the startDateTime index to narrow results first, then filter by ticketGroup
    // in memory. This is much faster than using the *ticketGroups multi-entry index
    if (startDateTimeAfter && startDateTimeBefore) {
      const filterFn = (ts: ITimeSlot) => {
        if (!ts.ticketGroups || !ts.ticketGroups.includes(ticketGroupId))
          return false;
        if (endDateTimeAfter && ts.endDateTime < endDateTimeAfter) return false;
        if (endDateTimeBefore && ts.endDateTime >= endDateTimeBefore)
          return false;
        return true;
      };

      results = await dexieDB.timeslots
        .where("startDateTime")
        .between(effectiveStartAfter, startDateTimeBefore, true, false)
        .filter(filterFn)
        .sortBy("startDateTime");
    } else {
      // Fallback: use multi-entry index on ticketGroups (broader queries without
      // tight date range, e.g. "all future timeslots for this ticket group")
      const needsDateFilter = !!(
        startDateTimeAfter ||
        startDateTimeBefore ||
        endDateTimeAfter ||
        endDateTimeBefore
      );

      const filterFn = (ts: ITimeSlot) => {
        if (ts.startDateTime < effectiveStartAfter) return false;
        if (startDateTimeBefore && ts.startDateTime >= startDateTimeBefore)
          return false;
        if (endDateTimeAfter && ts.endDateTime < endDateTimeAfter) return false;
        if (endDateTimeBefore && ts.endDateTime >= endDateTimeBefore)
          return false;
        return true;
      };

      if (needsDateFilter) {
        results = await dexieDB.timeslots
          .where("ticketGroups")
          .equals(ticketGroupId)
          .filter(filterFn)
          .sortBy("startDateTime");
      } else {
        results = await dexieDB.timeslots
          .where("ticketGroups")
          .equals(ticketGroupId)
          .sortBy("startDateTime");
      }
    }

    // Apply pagination after sorting
    if (skip > 0) {
      results = results.slice(skip);
    }
    if (limit !== undefined) {
      results = results.slice(0, limit);
    }

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.queryTimeslotsByTicketGroup", duration);

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.queryTimeslotsByTicketGroup",
      { params: options, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get timeslots by IDs
 * Uses direct primary key lookup - O(1) per ID
 *
 * @param ids - Array of timeslot IDs
 * @returns Array of timeslots (order not guaranteed)
 */
export async function getTimeslotsByIds(ids: number[]): Promise<ITimeSlot[]> {
  if (!ids || ids.length === 0) {
    return [];
  }

  // Filter out null, undefined, and NaN values
  const validIds = ids.filter(
    (id): id is number => id != null && !Number.isNaN(id)
  );

  if (validIds.length === 0) {
    return [];
  }

  const t1 = performance.now();

  try {
    const results = await dexieDB.timeslots
      .where("id")
      .anyOf(validIds)
      .toArray();

    const duration = performance.now() - t1;
    recordOperationDuration("dexie.getTimeslotsByIds", duration);

    return results;
  } catch (error) {
    LogService.info(
      "Error in dexie.getTimeslotsByIds",
      { params: { ids }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Get a single timeslot by ID
 * Uses direct primary key lookup - O(1)
 *
 * @param id - Timeslot ID
 * @returns The timeslot or undefined if not found
 */
export async function getTimeslotById(
  id: number
): Promise<ITimeSlot | undefined> {
  try {
    return await dexieDB.timeslots.get(id);
  } catch (error) {
    LogService.info(
      "Error in dexie.getTimeslotById",
      { params: { id }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Bulk insert or update timeslots
 * Uses bulkPut which inserts new records or updates existing ones by primary key
 *
 * @param timeslots - Array of timeslots to insert/update
 */
export async function bulkUpsertTimeslots(
  timeslots: Omit<ITimeSlot, "_id">[]
): Promise<void> {
  if (!timeslots || timeslots.length === 0) {
    return;
  }

  const t1 = performance.now();

  try {
    await dexieDB.timeslots.bulkPut(timeslots as ITimeSlot[]);

    const duration = performance.now() - t1;
    LogService.info(
      `dexie.bulkUpsertTimeslots: ${
        timeslots.length
      } timeslots in ${duration.toFixed(2)}ms`
    );
    recordOperationDuration("dexie.bulkUpsertTimeslots", duration);
  } catch (error) {
    LogService.info(
      "Error in dexie.bulkUpsertTimeslots",
      { params: { count: timeslots.length }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Update timeslot availability fields
 * Fetches existing records, updates fields, and uses bulkPut
 *
 * @param updates - Array of updates with id and new values
 */
export async function updateTimeslotsAvailability(
  updates: Array<{
    id: number;
    availability: number;
    booked: number;
    bookable?: number;
    availabilityUpdated?: string;
  }>
): Promise<void> {
  if (!updates || updates.length === 0) {
    return;
  }

  const t1 = performance.now();

  try {
    // Fetch existing timeslots using primary key index
    const ids = updates.map((u) => u.id);
    const existingTimeslots = await dexieDB.timeslots
      .where("id")
      .anyOf(ids)
      .toArray();

    // Build update map for O(1) lookup
    const updateMap = new Map(updates.map((u) => [u.id, u]));

    // Apply updates
    const updatedTimeslots = existingTimeslots.map((ts) => {
      const update = updateMap.get(ts.id);
      if (update) {
        return {
          ...ts,
          availability: update.availability,
          booked: update.booked,
          ...(update.bookable !== undefined && { bookable: update.bookable }),
          ...(update.availabilityUpdated && {
            availabilityUpdated: update.availabilityUpdated,
          }),
        };
      }
      return ts;
    });

    // Bulk update
    await dexieDB.timeslots.bulkPut(updatedTimeslots);

    const duration = performance.now() - t1;
    LogService.info(
      `dexie.updateTimeslotAvailability: ${
        updates.length
      } timeslots in ${duration.toFixed(2)}ms`
    );
    recordOperationDuration("dexie.updateTimeslotAvailability", duration);
  } catch (error) {
    LogService.info(
      "Error in dexie.updateTimeslotAvailability",
      { params: { count: updates.length }, error },
      LogLevel.Error
    );
    throw error;
  }
}

/**
 * Purge timeslots that have ended before the start of today (local time).
 * Timeslots that started before today but haven't ended yet are kept
 * since they may still be bookable.
 * Uses the endDateTime index for an efficient range delete.
 */
export async function purgeExpiredTimeslots(): Promise<void> {
  const t1 = performance.now();

  try {
    const startOfToday = dayjs().startOf("day").toISOString();

    const count = await dexieDB.timeslots
      .where("endDateTime")
      .below(startOfToday)
      .delete();

    const duration = performance.now() - t1;
    LogService.info(
      `dexie.purgeExpiredTimeslots: Removed ${count} expired timeslots in ${duration.toFixed(
        2
      )}ms`
    );
    recordOperationDuration("dexie.purgeExpiredTimeslots", duration);
  } catch (error) {
    LogService.info(
      "Error in dexie.purgeExpiredTimeslots",
      { error },
      LogLevel.Error
    );
  }
}

/**
 * Clear all timeslots from the database
 */
export async function clearTimeslotsCollection(): Promise<void> {
  const t1 = performance.now();

  try {
    await dexieDB.timeslots.clear();

    const duration = performance.now() - t1;
    LogService.info(
      `dexie.clearTimeslots: Cleared in ${duration.toFixed(2)}ms`
    );
    recordOperationDuration("dexie.clearTimeslots", duration);
  } catch (error) {
    LogService.info("Error in dexie.clearTimeslots", { error }, LogLevel.Error);
    throw error;
  }
}
